import React, { useState, useEffect, useCallback } from 'react';
import agentActions from '../../../../actions/agent';
import Modal from '../../../../components/Modal';
import { C } from '../agentTheme';

function Badge({ color, bg, children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 7px', borderRadius: 5,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
      color, background: bg, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function StatBox({ label, value, sub, color }) {
  return (
    <div style={{
      flex: 1, minWidth: 100,
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: '10px 14px',
    }}>
      <div style={{ fontSize: 10, color: C.textDim, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || C.text, fontFamily: C.mono }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children, badge }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 16 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '6px 0', marginBottom: open ? 8 : 0,
        }}
      >
        <span style={{ fontSize: 10, color: C.textDim, transition: 'transform 150ms', transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textMuted }}>{title}</span>
        {badge && <span style={{ marginLeft: 'auto' }}>{badge}</span>}
      </button>
      {open && children}
    </div>
  );
}

function fmtMs(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtTokens(n) {
  if (n == null) return '—';
  if (n >= 1000) return `~${(n / 1000).toFixed(1)}k`;
  return `~${n}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function durationColor(ms) {
  if (ms == null) return C.textMuted;
  if (ms < 5000) return C.green;
  if (ms < 15000) return C.yellow;
  return C.red;
}

function MessageRow({ msg, idx }) {
  const [open, setOpen] = useState(false);
  const isUser = msg.role === 'user';
  const roleColor = isUser ? C.blue : C.accent;

  return (
    <div style={{
      borderBottom: `1px solid ${C.border}`,
      padding: '8px 0',
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ fontSize: 9, color: C.textDim, fontFamily: C.mono, minWidth: 20, textAlign: 'right' }}>{idx + 1}</span>
        <Badge color={roleColor} bg={isUser ? C.blueBg : C.accentBg}>
          {msg.role.toUpperCase()}
        </Badge>
        {msg.toolCalls?.length > 0 && msg.toolCalls.map((t, i) => (
          <Badge key={i} color={C.purple} bg={C.purpleBg}>{t}</Badge>
        ))}
        <span style={{ flex: 1, fontSize: 11, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {msg.preview || '—'}
        </span>
        <span style={{ fontSize: 10, color: C.textDim, fontFamily: C.mono, minWidth: 48, textAlign: 'right' }}>
          {fmtTokens(msg.tokenEstimate)}
        </span>
        {msg.role === 'assistant' && (
          <span style={{ fontSize: 11, fontWeight: 700, color: durationColor(msg.durationMs), fontFamily: C.mono, minWidth: 52, textAlign: 'right' }}>
            {fmtMs(msg.durationMs)}
          </span>
        )}
        <span style={{ fontSize: 9, color: C.textDim, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }}>▶</span>
      </div>

      {open && (
        <div style={{ marginTop: 8, paddingLeft: 28, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: C.textMuted }}>ID: <span style={{ color: C.text, fontFamily: C.mono }}>{msg.id}</span></span>
            <span style={{ fontSize: 10, color: C.textMuted }}>Created: <span style={{ color: C.text }}>{fmtDate(msg.createdAt)}</span></span>
            <span style={{ fontSize: 10, color: C.textMuted }}>Content length: <span style={{ color: C.text, fontFamily: C.mono }}>{msg.contentLength?.toLocaleString()} chars</span></span>
            <span style={{ fontSize: 10, color: C.textMuted }}>Cumulative ctx: <span style={{ color: C.accent, fontFamily: C.mono }}>{fmtTokens(msg.cumulativeTokens)}</span></span>
          </div>
          {msg.role === 'assistant' && msg.durationMs != null && (
            <div style={{ fontSize: 10, marginTop: 2 }}>
              <span style={{ color: C.textMuted }}>Response time: </span>
              <span style={{ color: durationColor(msg.durationMs), fontWeight: 700, fontFamily: C.mono }}>{fmtMs(msg.durationMs)}</span>
              {msg.durationMs > 10000 && (
                <span style={{ color: C.textDim, marginLeft: 8 }}>
                  {msg.toolCalls?.length > 0
                    ? `↳ ${msg.toolCalls.length} tool call${msg.toolCalls.length > 1 ? 's' : ''} = ${msg.toolCalls.length} extra LLM round-trip${msg.toolCalls.length > 1 ? 's' : ''}`
                    : '↳ no tool calls — check context size or model latency'}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LangfuseTrace({ trace }) {
  const [open, setOpen] = useState(false);
  const totalMs = trace.latency != null ? trace.latency * 1000 : null;
  const inputTokens = trace.usage?.input || trace.totalObservationCost?.input;
  const outputTokens = trace.usage?.output || trace.totalObservationCost?.output;

  const generations = (trace.observations || []).filter(o => o.type === 'GENERATION');
  const spans = (trace.observations || []).filter(o => o.type === 'SPAN');

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 6, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ fontSize: 9, color: C.textDim, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms', display: 'inline-block' }}>▶</span>
        <span style={{ fontSize: 11, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: C.mono }}>
          {trace.name || trace.id}
        </span>
        {totalMs != null && (
          <span style={{ fontSize: 11, fontWeight: 700, color: durationColor(totalMs), fontFamily: C.mono }}>
            {fmtMs(totalMs)}
          </span>
        )}
        {(inputTokens || outputTokens) && (
          <span style={{ fontSize: 10, color: C.textMuted, fontFamily: C.mono }}>
            {inputTokens ?? '?'}↑ {outputTokens ?? '?'}↓
          </span>
        )}
        <span style={{ fontSize: 10, color: C.textDim }}>{fmtDate(trace.timestamp)}</span>
      </div>

      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Metadata */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: C.textMuted }}>Trace ID: <span style={{ color: C.text, fontFamily: C.mono, fontSize: 9 }}>{trace.id}</span></span>
            {trace.sessionId && <span style={{ fontSize: 10, color: C.textMuted }}>Session: <span style={{ color: C.text, fontFamily: C.mono, fontSize: 9 }}>{trace.sessionId}</span></span>}
            {trace.userId && <span style={{ fontSize: 10, color: C.textMuted }}>User: <span style={{ color: C.text }}>{trace.userId}</span></span>}
          </div>

          {/* Generations (LLM calls) */}
          {generations.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                LLM Calls ({generations.length})
              </div>
              {generations.map((gen, i) => {
                const genMs = gen.latency != null ? gen.latency * 1000 : null;
                return (
                  <div key={i} style={{
                    padding: '6px 10px', background: C.accentBg, borderRadius: 6, marginBottom: 4,
                    border: `1px solid rgba(129,140,248,0.15)`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Badge color={C.accent} bg="rgba(129,140,248,0.15)">{gen.model || 'unknown model'}</Badge>
                      <span style={{ fontSize: 10, color: C.textMuted, fontFamily: C.mono }}>
                        {gen.usage?.input ?? '?'} in · {gen.usage?.output ?? '?'} out · {gen.usage?.total ?? '?'} total tokens
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: durationColor(genMs), fontFamily: C.mono }}>
                        {fmtMs(genMs)}
                      </span>
                    </div>
                    {gen.name && gen.name !== trace.name && (
                      <div style={{ fontSize: 10, color: C.textDim, marginTop: 3 }}>{gen.name}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Tool call spans */}
          {spans.filter(s => s.name?.toLowerCase().includes('tool')).length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                Tool Spans
              </div>
              {spans.filter(s => s.name?.toLowerCase().includes('tool')).map((span, i) => {
                const spanMs = span.latency != null ? span.latency * 1000 : null;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
                    <Badge color={C.purple} bg={C.purpleBg}>{span.name}</Badge>
                    <span style={{ fontSize: 10, color: durationColor(spanMs), fontFamily: C.mono }}>{fmtMs(spanMs)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Raw trace metadata */}
          {trace.metadata && Object.keys(trace.metadata).length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Metadata</div>
              <pre style={{ fontSize: 9, color: C.textMuted, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: C.mono }}>
                {JSON.stringify(trace.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ThreadInfoModal({ threadId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('messages');

  const load = useCallback(() => {
    if (!threadId) return;
    setLoading(true);
    setError(null);
    agentActions.getThreadDebug(threadId)
      .then(res => {
        if (res?.success) setData(res.data);
        else setError(res?.error || 'Failed to load');
      })
      .catch(e => setError(e?.error || e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  const { thread, messages = [], stats, langfuse } = data || {};

  const tabs = [
    { id: 'messages', label: `Messages${stats ? ` (${stats.messageCount})` : ''}` },
    { id: 'langfuse', label: `Langfuse${langfuse?.traceCount != null ? ` (${langfuse.traceCount})` : ''}` },
    { id: 'meta', label: 'Thread Meta' },
  ];

  return (
    <Modal open={true} onClose={onClose} width={820} aria-label="Thread Debug">
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 20px',
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
        }}>
          <Badge color={C.yellow} bg={C.yellowBg}>DEV</Badge>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Thread Debug
            {thread?.title && <span style={{ fontWeight: 400, color: C.textMuted, marginLeft: 8 }}>— {thread.title}</span>}
          </span>
          <button
            onClick={load}
            title="Refresh"
            style={{ background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}
          >↻ Refresh</button>
          <button
            onClick={onClose}
            aria-label="Close thread details"
            style={{ background: 'none', border: 'none', color: C.textMuted, fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
          >×</button>
        </div>

        {/* Stats bar */}
        {stats && (
          <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, flexWrap: 'wrap' }}>
            <StatBox label="Messages" value={stats.messageCount} sub={`${stats.userMessages}u · ${stats.assistantMessages}a`} />
            <StatBox label="Context (est.)" value={fmtTokens(stats.totalTokenEstimate)} sub="~4 chars/token" color={stats.totalTokenEstimate > 6000 ? C.red : stats.totalTokenEstimate > 3000 ? C.yellow : C.green} />
            <StatBox label="Avg Response" value={stats.avgAssistantDurationFmt} color={stats.avgAssistantDurationMs > 15000 ? C.red : stats.avgAssistantDurationMs > 8000 ? C.yellow : C.green} />
            <StatBox label="Max Response" value={stats.maxAssistantDurationFmt} color={stats.maxAssistantDurationMs > 15000 ? C.red : stats.maxAssistantDurationMs > 8000 ? C.yellow : C.green} />
            {langfuse?.available && (
              <StatBox label="Langfuse" value={`${langfuse.traceCount ?? 0} traces`} color={C.accent} />
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, padding: '0 20px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '10px 14px',
                fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
                color: activeTab === t.id ? C.accent : C.textMuted,
                borderBottom: activeTab === t.id ? `2px solid ${C.accent}` : '2px solid transparent',
                transition: 'all 150ms ease',
              }}
            >{t.label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, color: C.textDim, fontSize: 13 }}>
              Loading debug info…
            </div>
          )}
          {error && (
            <div style={{ padding: 16, background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 8, color: C.red, fontSize: 12 }}>
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Messages tab */}
              {activeTab === 'messages' && (
                <div>
                  <div style={{ display: 'flex', gap: 8, fontSize: 10, color: C.textDim, padding: '0 0 8px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
                    <span style={{ minWidth: 20 }}>#</span>
                    <span style={{ minWidth: 72 }}>Role</span>
                    <span style={{ flex: 1 }}>Preview</span>
                    <span style={{ minWidth: 48, textAlign: 'right' }}>Tokens</span>
                    <span style={{ minWidth: 52, textAlign: 'right' }}>Duration</span>
                    <span style={{ minWidth: 16 }} />
                  </div>
                  {messages.map((msg, idx) => (
                    <MessageRow key={msg.id} msg={msg} idx={idx} />
                  ))}
                  {messages.length === 0 && (
                    <div style={{ color: C.textDim, fontSize: 12, padding: '16px 0' }}>No messages found in PG.</div>
                  )}
                </div>
              )}

              {/* Langfuse tab */}
              {activeTab === 'langfuse' && (
                <div>
                  {!langfuse?.available ? (
                    <div style={{ color: C.textDim, fontSize: 12, padding: '16px 0' }}>
                      Langfuse not configured or keys not set.
                    </div>
                  ) : langfuse.traces?.length === 0 ? (
                    <div style={{ color: C.textDim, fontSize: 12, padding: '16px 0' }}>
                      No Langfuse traces found for this thread's time range.
                      <br /><br />
                      <span style={{ color: C.textMuted }}>
                        Traces are matched by sessionId = threadId. If Mastra doesn't tag traces with the threadId, they may not appear here.
                        Check Langfuse dashboard directly: <a href="https://cloud.langfuse.com" target="_blank" rel="noreferrer" style={{ color: C.accent }}>cloud.langfuse.com</a>
                      </span>
                    </div>
                  ) : (
                    langfuse.traces.map((trace, i) => (
                      <LangfuseTrace key={trace.id || i} trace={trace} />
                    ))
                  )}
                </div>
              )}

              {/* Meta tab */}
              {activeTab === 'meta' && thread && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Section title="Thread">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[
                        ['ID', thread.id],
                        ['Title', thread.title],
                        ['Created', fmtDate(thread.createdAt)],
                        ['Updated', fmtDate(thread.updatedAt)],
                      ].map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', gap: 12 }}>
                          <span style={{ fontSize: 11, color: C.textDim, minWidth: 80 }}>{k}</span>
                          <span style={{ fontSize: k === 'ID' ? 10 : 11, color: C.text, fontFamily: k === 'ID' ? C.mono : 'inherit' }}>{v || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </Section>

                  <Section title="Metadata">
                    <pre style={{
                      fontSize: 11, color: C.textMuted, margin: 0,
                      background: C.surface, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                      fontFamily: C.mono, lineHeight: 1.6,
                    }}>
                      {JSON.stringify(thread.metadata || {}, null, 2)}
                    </pre>
                  </Section>

                  {thread.metadata?.image_attachments && (
                    <Section title="Image Attachments (stored in thread)">
                      {(() => {
                        let attachments = [];
                        try { attachments = JSON.parse(thread.metadata.image_attachments); } catch { return null; }
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {attachments.map((a, i) => (
                              <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '6px 10px', background: C.surface, borderRadius: 6,
                                border: `1px solid ${C.border}`,
                              }}>
                                <img src={a.url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                                <div>
                                  <div style={{ fontSize: 10, color: C.text }}>{a.name || '—'}</div>
                                  <div style={{ fontSize: 9, color: C.textDim, fontFamily: C.mono, wordBreak: 'break-all' }}>{a.url}</div>
                                  <Badge color={a.role === 'auto' ? C.textMuted : C.accent} bg={C.accentBg}>{a.role || 'auto'}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </Section>
                  )}
                </div>
              )}
            </>
          )}
        </div>
    </Modal>
  );
}
